import { useParams } from '@tanstack/react-router';
import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { AbandonedCheckoutDetail } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { OrderAddressCard, OrderLink } from '@/features/orders';
import { useStore } from '@/shared/store/store-context';

import { useAbandonedCheckout } from '../hooks/use-abandoned-checkout';
import { useRecoveryTemplateEnabled } from '../hooks/use-recovery-template-enabled';
import { useSendRecoveryAction } from '../hooks/use-send-recovery-action';
import { recoveryState, unsendableHint } from '../schemas/checkout-rules';

const ROUTE = '/_authed/_active/s/$storeId/abandoned-checkouts/$checkoutId' as const;

function LinesCard({ checkout }: { checkout: AbandonedCheckoutDetail }) {
  const { t } = useTranslation('abandonedCheckouts');
  const format = useFormat();
  return (
    <Card className="gap-0 py-0" data-tour="abandoned.detail.lines">
      <CardHeader className="border-b py-4">
        <CardTitle>{t('detail.lines')}</CardTitle>
      </CardHeader>
      <CardContent className="px-0">
        {checkout.lines.length === 0 ? (
          <p className="text-muted-foreground px-6 py-6 text-sm">{t('detail.noLines')}</p>
        ) : (
          <Table aria-label={t('detail.lines')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">{t('detail.item')}</TableHead>
                <TableHead className="text-right">{t('detail.quantity')}</TableHead>
                <TableHead className="pr-6 text-right">{t('detail.lineTotal')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {checkout.lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell className="pl-6 align-top whitespace-normal">
                    <p className="font-medium">{line.title}</p>
                    <p className="text-muted-foreground text-xs">
                      {[line.variantTitle, line.sku].filter(Boolean).join(' · ')}
                    </p>
                    {line.customizations.length > 0 ? (
                      <ul
                        className="flex flex-wrap gap-1 pt-1"
                        aria-label={t('detail.customizations')}
                      >
                        {line.customizations.map((c) => (
                          <li key={c.key}>
                            <Badge variant="secondary" className="font-normal">
                              {`${c.label}: ${c.file?.filename ?? c.valueLabel ?? String(c.value)}`}
                            </Badge>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right align-top tabular-nums">
                    {line.quantity}
                  </TableCell>
                  <TableCell className="pr-6 text-right align-top font-medium tabular-nums">
                    {format.money(line.linePrice)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function TotalsCard({ checkout }: { checkout: AbandonedCheckoutDetail }) {
  const { t } = useTranslation('abandonedCheckouts');
  const format = useFormat();
  const rows: Array<[string, string]> = [
    [t('detail.subtotal'), format.money(checkout.subtotal)],
    ...(checkout.discount.amount > 0
      ? [
          [
            checkout.discountCodes.length > 0
              ? t('detail.discountWith', { codes: checkout.discountCodes.join(', ') })
              : t('detail.discount'),
            `−${format.money(checkout.discount)}`,
          ] as [string, string],
        ]
      : []),
    [
      checkout.shippingLine
        ? t('detail.shippingWith', { method: checkout.shippingLine.title })
        : t('detail.shipping'),
      format.money(checkout.shipping),
    ],
    [t('detail.tax'), format.money(checkout.tax)],
  ];
  return (
    <Card className="gap-3">
      <CardHeader>
        <CardTitle>{t('detail.summary')}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1.5 text-sm">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="text-right tabular-nums">{value}</dd>
            </div>
          ))}
          <dt className="border-t pt-2 font-medium">{t('detail.total')}</dt>
          <dd className="border-t pt-2 text-right font-medium tabular-nums">
            {format.money(checkout.total)}
          </dd>
        </dl>
      </CardContent>
    </Card>
  );
}

function RecoveryCard({ checkout }: { checkout: AbandonedCheckoutDetail }) {
  const { t } = useTranslation('abandonedCheckouts');
  const format = useFormat();
  const store = useStore();
  return (
    <Card className="gap-3" data-tour="abandoned.detail.recovery">
      <CardHeader>
        <CardTitle>{t('detail.recovery')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 text-sm">
        {checkout.recoveredOrderId ? (
          <p className="flex flex-wrap items-center gap-1">
            {t('detail.recoveredAs')}
            <OrderLink
              storeId={store.id}
              orderId={checkout.recoveredOrderId}
              name={checkout.recoveredOrderName}
            />
          </p>
        ) : null}
        {checkout.recoveryEmails.length === 0 ? (
          <p className="text-muted-foreground">{t('detail.noRecoveryEmails')}</p>
        ) : (
          <ol aria-label={t('detail.recoveryEmails')} className="flex flex-col gap-1">
            {checkout.recoveryEmails.map((email) => (
              <li key={email.sentAt} title={format.utc(email.sentAt)}>
                {t('detail.emailSentOn', { when: format.dateTime(email.sentAt) })}
              </li>
            ))}
          </ol>
        )}
        <p className="text-muted-foreground" title={format.utc(checkout.expiresAt)}>
          {recoveryState(checkout) === 'open'
            ? t('detail.expires', { when: format.relative(checkout.expiresAt) })
            : t('detail.expired', { when: format.relative(checkout.expiresAt) })}
        </p>
      </CardContent>
    </Card>
  );
}

function ContactCard({ checkout }: { checkout: AbandonedCheckoutDetail }) {
  const { t } = useTranslation('abandonedCheckouts');
  return (
    <Card className="gap-3" data-tour="abandoned.detail.contact">
      <CardHeader>
        <CardTitle>{t('detail.contact')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1 text-sm">
        <p className="break-all">{checkout.email}</p>
        {checkout.phone ? <p>{checkout.phone}</p> : null}
        <p className="text-muted-foreground">
          {checkout.customerId ? t('detail.hasAccount') : t('detail.guest')}
        </p>
        <p className="text-muted-foreground">
          {checkout.acceptsMarketing ? t('detail.acceptsMarketing') : t('detail.noMarketing')}
        </p>
      </CardContent>
    </Card>
  );
}

function CheckoutDetail({ checkout }: { checkout: AbandonedCheckoutDetail }) {
  const { t } = useTranslation('abandonedCheckouts');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const { onSend, sendingId } = useSendRecoveryAction(store.id);
  const templateEnabled = useRecoveryTemplateEnabled(store.id);
  const state = recoveryState(checkout);
  const hint = unsendableHint(checkout, templateEnabled);

  return (
    <PageContent>
      <PageHeader
        title={checkout.email}
        meta={<Badge variant="outline">{t(`state.${state}`)}</Badge>}
        description={
          <span title={format.utc(checkout.lastActivityAt)}>
            {t('detail.lastActivity', {
              when: format.relative(checkout.lastActivityAt),
              started: format.dateTime(checkout.createdAt),
            })}
          </span>
        }
        backTo={{ to: '/s/$storeId/abandoned-checkouts', params: { storeId: store.id } }}
        backLabel={t('detail.back')}
        actions={
          can('orders.write') && checkout.canSendRecovery ? (
            <Button
              disabled={sendingId === checkout.id}
              onClick={() => onSend(checkout.id)}
              data-tour="abandoned.detail.send"
            >
              <Send aria-hidden />
              {checkout.recoveryEmailSentAt ? t('recovery.resend') : t('recovery.send')}
            </Button>
          ) : null
        }
      />
      {hint ? (
        <Alert role="status">
          <AlertDescription>{t(`recovery.${hint}Detail`)}</AlertDescription>
        </Alert>
      ) : null}
      <FormLayout
        aside={
          <>
            <ContactCard checkout={checkout} />
            <OrderAddressCard
              title={t('detail.shippingAddress')}
              address={checkout.shippingAddress}
            />
            <OrderAddressCard
              title={t('detail.billingAddress')}
              address={checkout.billingAddress ?? checkout.shippingAddress}
            />
          </>
        }
      >
        <LinesCard checkout={checkout} />
        <div className="grid gap-6 md:grid-cols-2">
          <TotalsCard checkout={checkout} />
          <RecoveryCard checkout={checkout} />
        </div>
      </FormLayout>
    </PageContent>
  );
}

function CheckoutLoader() {
  const store = useStore();
  const { checkoutId } = useParams({ from: ROUTE });
  const { data } = useAbandonedCheckout(store.id, checkoutId);
  return data ? <CheckoutDetail checkout={data} /> : null;
}

export function AbandonedCheckoutPage() {
  return (
    <FeatureErrorBoundary name="abandonedCheckouts.detail">
      <CheckoutLoader />
    </FeatureErrorBoundary>
  );
}
